import { cn } from "@magicweave/design";
import type { ReactNode } from "react";

import { userMarkFor } from "./user-mark";

// token-exempt: fixed deep grounds in the brand's green and night blue, the same in both themes
const GROUNDS = [
  "oklch(0.2 0.05 175)", // token-exempt: as above
  "oklch(0.2 0.055 258)", // token-exempt: as above
  "oklch(0.17 0.03 215)", // token-exempt: as above
  "oklch(0.24 0.012 265)", // token-exempt: as above
];
// token-exempt: head tones, brand green and light neutrals, all well above the dark features
const TONES = [
  "var(--color-accent)",
  "var(--color-art-ink)",
  "oklch(0.74 0.05 262)", // token-exempt: as above
  "oklch(0.86 0.11 152)", // token-exempt: as above
];
const INK = "var(--color-art-ground)";
const SHADE = "oklch(0.12 0.015 185 / 0.28)"; // token-exempt: the ink, as a cheek on the head

type R = readonly [x: number, y: number, w: number, h: number];

function rects(list: readonly R[], fill: string, key: string): ReactNode {
  return list.map(([x, y, w, h]) => (
    <rect
      key={`${key}${String(x)}.${String(y)}`}
      x={x}
      y={y}
      width={w}
      height={h}
      rx={0.6}
      fill={fill}
    />
  ));
}

/**
 * A person's avatar: a little blocky character, drawn from their id out of a
 * handful of rectangles. The ground and the features are the same in both
 * themes, so a face reads at 20px and at 64px alike. Decorative: the name
 * beside it, or the control's own label, says who it is.
 */
export function UserAvatar({
  id,
  size = 32,
  className,
}: {
  id: string;
  size?: number;
  className?: string;
}) {
  const m = userMarkFor(id);
  const x0 = 16 - m.w / 2;
  const top = 28 - m.h;
  const ex = (dx: number) => 16 + dx; // eye centre helpers
  const ey = top + Math.round(m.h * 0.32);
  const my = top + m.h - 5;
  const eyes: readonly R[][] = [
    [
      [ex(-5.5), ey, 3, 3],
      [ex(2.5), ey, 3, 3],
    ],
    [
      [ex(-5), ey - 1, 2, 5],
      [ex(3), ey - 1, 2, 5],
    ],
    [[ex(-6.5), ey, 13, 3]],
    [[ex(-2), ey - 1, 4, 4]],
    [
      [ex(-5.5), ey, 3, 3],
      [ex(2.5), ey + 1.5, 3, 1.5],
    ],
    [
      [ex(-6), ey + 1, 4, 2],
      [ex(2), ey + 1, 4, 2],
    ],
  ];
  const mouths: readonly R[][] = [
    [[ex(-4), my, 8, 2]],
    [[ex(-2), my - 1, 4, 3]],
    [
      [ex(-5), my, 3, 2],
      [ex(2), my, 3, 2],
    ],
    [
      [ex(-4), my, 8, 2],
      [ex(-5), my - 2, 2, 2],
      [ex(3), my - 2, 2, 2],
    ],
    [[ex(-4), my, 8, 2]],
  ];
  const tops: readonly R[][] = [
    [],
    [
      [15, top - 4, 2, 4],
      [14, top - 6, 4, 3],
    ],
    [
      [x0 + 2, top - 3, 3, 3],
      [x0 + m.w - 5, top - 3, 3, 3],
    ],
    [
      [x0 - 1, top - 2, m.w + 2, 2],
      [x0 + 3, top - 5, m.w - 6, 3],
    ],
    [
      [x0 + 3, top - 3, 3, 3],
      [15, top - 4, 3, 4],
      [x0 + m.w - 6, top - 3, 3, 3],
    ],
    [[x0 + 2, top + 2, m.w - 4, 2]],
  ];
  const sides: readonly R[][] = [
    [],
    [
      [x0 - 2, top + m.h / 2 - 2, 2, 4],
      [x0 + m.w, top + m.h / 2 - 2, 2, 4],
    ],
    [[x0 + m.w, top + m.h / 2 - 3, 2, 6]],
  ];
  const cheeks: readonly R[][] = [
    [],
    [
      [x0 + 1.5, my - 3, 3, 2],
      [x0 + m.w - 4.5, my - 3, 3, 2],
    ],
  ];
  const part = <T,>(list: readonly T[], i: number): T => list[i] ?? (list[0] as T);
  const antenna = m.top === 1 || m.top === 4;

  return (
    <span
      aria-hidden="true"
      data-user-avatar=""
      className={cn("inline-block shrink-0 overflow-hidden rounded-[24%]", className)}
      style={{ width: size, height: size, background: part(GROUNDS, m.ground) }}
    >
      <svg width="100%" height="100%" viewBox="0 0 32 32" className="block">
        {rects(part(sides, m.side), part(TONES, m.tone), "s")}
        {rects(part(tops, m.top), antenna ? part(TONES, m.tone) : "var(--color-accent)", "t")}
        <rect x={x0} y={top} width={m.w} height={m.h} rx={1.5} fill={part(TONES, m.tone)} />
        {rects(part(cheeks, m.cheek), SHADE, "c")}
        {rects(part(eyes, m.eyes), INK, "e")}
        {rects(part(mouths, m.mouth), INK, "m")}
        {m.mouth === 4 ? (
          <rect x={15} y={my} width={2} height={2} fill={part(TONES, 1)} />
        ) : null}
      </svg>
    </span>
  );
}
